import * as React from "react"
import { Pressable } from "react-native"

import { router } from "expo-router"

import { useAuth } from "src/context/AuthContext"
import { useMediaContext } from "src/context/MediaContextProvider"
import { MediaType } from "src/utils/mediaTypes"
import { openWebsite } from "src/utils/openWebsite"
import { parseTimestampToSeconds } from "src/utils/time"
import { getYouTubeVideoId, isYouTubeVideo } from "src/utils/youtubeUtils"
import { CrisisLineThumbnail } from "../CrisisLine/CrisisLineThumbnail"
import { AudioThumbnail } from "./AudioThumbnail"
import { ImageThumbnail } from "./ImageThumbnail"
import { ResourceThumbnail } from "./ResourceThumbnail"
import { VideoThumbnail } from "./VideoThumbnail"
import { WebsiteThumbnail } from "./WebThumbnail"

interface MediaThumbnailProps {
  mediaType: MediaType
  /**
   * Priority for thumbnail generation queue.
   * Higher values are processed first. Use message timestamp for recency-based ordering.
   */
  priority?: number
  /**
   * The active_task_id from the conversation this message belongs to.
   * Used as a fallback task ID when the event itself doesn't carry task_id or id,
   * enabling thumbnail refresh for old conversation-history events.
   */
  conversationTaskId?: string
}

export const MediaThumbnail = ({
  mediaType: originalMediaType,
  priority = 0,
  conversationTaskId,
}: MediaThumbnailProps) => {
  const { getLastPosition } = useMediaContext()
  const { api, apiHost } = useAuth()

  // Holds refreshed signed URLs after the originals from conversation history have expired.
  // Populated on demand when a thumbnail or media load reports an error.
  const [refreshedThumbnailUrl, setRefreshedThumbnailUrl] = React.useState<string | undefined>(
    undefined,
  )
  const [refreshedMediaUrl, setRefreshedMediaUrl] = React.useState<string | undefined>(undefined)
  const [refreshedHlsUrl, setRefreshedHlsUrl] = React.useState<string | undefined>(undefined)
  const isRefreshingRef = React.useRef(false)

  const mediaType = React.useMemo((): MediaType => {
    // Remap YouTube website links to video type so VideoThumbnail is used
    if (
      originalMediaType.type === "website" &&
      (originalMediaType.url.includes("youtube.com") || originalMediaType.url.includes("youtu.be"))
    ) {
      return {
        type: "video",
        url: originalMediaType.url,
        id: originalMediaType.id,
        title: originalMediaType.title,
      }
    }
    // Remap OpenResource youtube type to video type for consistent thumbnail rendering
    if (originalMediaType.type === "resource" && originalMediaType.resourceType === "youtube") {
      return {
        type: "video",
        url: originalMediaType.url,
        id: originalMediaType.id,
        title: originalMediaType.title,
        offset: originalMediaType.offset,
      }
    }
    return originalMediaType
  }, [originalMediaType])

  /**
   * Called when a thumbnail image fails to load (video or resource types).
   * Fetches fresh signed URLs for the thumbnail, media file, and (for video) HLS playlist.
   * Tries two lookups in order until one returns resources:
   *   1. task_id  — mediaType.taskId, then conversationTaskId
   *   2. step_id  — mediaType.id (event_args.id is likely a step ID on older events)
   * For resource types, matches by resource_id first, then falls back to type matching.
   */
  const handleThumbnailError = React.useCallback(async () => {
    if (mediaType.type !== "video" && mediaType.type !== "resource") return
    if (isRefreshingRef.current) return
    isRefreshingRef.current = true
    try {
      const taskId = mediaType.taskId ?? conversationTaskId
      const candidates: Array<{ taskId: string } | { stepId: string }> = []
      if (taskId) candidates.push({ taskId })
      if (mediaType.id) candidates.push({ stepId: mediaType.id })

      if (candidates.length === 0) return

      const base = `https://${apiHost}`
      const toUrl = (key: string) => new URL(`files/${key}`, base).href

      for (const params of candidates) {
        const listResult = await api.getContentResources(params)
        if (listResult.kind !== "ok" || listResult.resources.length === 0) {
          console.warn(
            "[MediaThumbnail] Empty or failed resources lookup:",
            params,
            listResult.kind !== "ok" ? listResult.kind : "empty list",
          )
          continue
        }

        const targetType = mediaType.type === "resource" ? mediaType.resourceType : "video"
        const resource =
          listResult.resources.find((r) => r.resource_id === mediaType.id) ??
          listResult.resources.find((r) => r.type === targetType) ??
          listResult.resources[0]

        if (resource.thumbnail_s3_key) setRefreshedThumbnailUrl(toUrl(resource.thumbnail_s3_key))
        if (resource.data) setRefreshedMediaUrl(toUrl(resource.data))
        if (resource.playlist_s3_key) setRefreshedHlsUrl(toUrl(resource.playlist_s3_key))

        if (!resource.thumbnail_s3_key && !resource.data) {
          console.warn("[MediaThumbnail] No signed URLs in resource:", resource.resource_id)
        }
        break
      }
    } finally {
      isRefreshingRef.current = false
    }
  }, [mediaType, api, apiHost, conversationTaskId])

  const canRefreshThumbnail =
    (mediaType.type === "video" || mediaType.type === "resource") &&
    !!(
      (mediaType.type === "video" ? mediaType.taskId : mediaType.taskId) ??
      mediaType.id ??
      conversationTaskId
    )

  // For website events the server sends a resource id but no thumbnail URL.
  // Fetch it proactively on mount so the chat bubble can show a real image
  // instead of always falling through to the OG-scrape path.
  React.useEffect(() => {
    if (mediaType.type !== "website") return
    if (!mediaType.id) return
    if (mediaType.thumbnail) return // server already supplied one
    if (isRefreshingRef.current) return
    isRefreshingRef.current = true

    const base = `https://${apiHost}`
    const toUrl = (key: string) => new URL(`files/${key}`, base).href

    api
      .getContentResources({ stepId: mediaType.id })
      .then((result) => {
        if (result.kind !== "ok" || result.resources.length === 0) return
        const resource =
          result.resources.find((r) => r.resource_id === mediaType.id) ?? result.resources[0]
        if (resource.thumbnail_s3_key) setRefreshedThumbnailUrl(toUrl(resource.thumbnail_s3_key))
      })
      .catch((err) => {
        console.warn("[MediaThumbnail] Failed to fetch website thumbnail:", err)
      })
      .finally(() => {
        isRefreshingRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mediaType.type,
    mediaType.type === "website" ? mediaType.id : undefined,
    mediaType.type === "website" ? mediaType.thumbnail : undefined,
    api,
    apiHost,
  ])

  const Component = React.useCallback(() => {
    switch (mediaType.type) {
      case "video":
        // Use backend thumbnail when provided; otherwise use URL for local thumbnail generation (HLS m3u8 doesn't work)
        return (
          <VideoThumbnail
            videoUrl={(refreshedHlsUrl ?? mediaType.hlsUrl) || (refreshedMediaUrl ?? mediaType.url)}
            thumbnailUrl={refreshedMediaUrl ?? mediaType.url}
            backendThumbnailUrl={refreshedThumbnailUrl ?? mediaType.thumbnail}
            onThumbnailError={canRefreshThumbnail ? handleThumbnailError : undefined}
            priority={priority}
          />
        )
      case "image":
        return <ImageThumbnail imageUrl={mediaType.url} />
      case "audio":
        return <AudioThumbnail title={mediaType.title} />
      case "resource":
        return (
          <ResourceThumbnail
            resourceType={mediaType.resourceType}
            resourceId={mediaType.id}
            title={mediaType.title}
            thumbnailUrl={refreshedThumbnailUrl ?? mediaType.thumbnail}
            onThumbnailError={canRefreshThumbnail ? handleThumbnailError : undefined}
          />
        )
      case "website":
        return (
          <WebsiteThumbnail
            websiteUrl={mediaType.url}
            thumbnailUrl={refreshedThumbnailUrl ?? mediaType.thumbnail}
          />
        )
      case "todoList":
        return null // no thumbnail for todo list events
      case "crisisLine":
        return <CrisisLineThumbnail />
      default:
        return <></>
    }
  }, [
    mediaType,
    priority,
    refreshedThumbnailUrl,
    refreshedMediaUrl,
    refreshedHlsUrl,
    handleThumbnailError,
    canRefreshThumbnail,
  ])

  const handlePress = () => {
    if (!mediaType) return

    switch (mediaType.type) {
      case "video":
        // eslint-disable-next-line no-case-declarations
        const isYouTube = isYouTubeVideo(mediaType.url)
        // For YouTube: prefer UUID resourceId as key (for OpenResource events); fall back to URL
        // eslint-disable-next-line no-case-declarations
        const resourceKey = isYouTube
          ? mediaType.id || mediaType.url
          : mediaType.id || mediaType.url || ""
        // Priority: 1) Saved local position 2) Backend offset 3) Start from beginning
        // eslint-disable-next-line no-case-declarations
        const savedPosition = getLastPosition(resourceKey)
        // eslint-disable-next-line no-case-declarations
        const startTime = savedPosition?.offset
          ? String(parseTimestampToSeconds(savedPosition.offset))
          : mediaType.offset
            ? String(parseTimestampToSeconds(mediaType.offset))
            : "0"
        if (isYouTube) {
          router.push({
            pathname: "/youtube-player",
            params: {
              videoId: getYouTubeVideoId(mediaType.url || ""),
              startTime,
              resourceId: mediaType.id,
              title: mediaType.title,
            },
          } as any)
        } else {
          router.push({
            pathname: "/video-player",
            params: {
              videoUrl:
                (refreshedHlsUrl ?? mediaType.hlsUrl) || (refreshedMediaUrl ?? mediaType.url) || "",
              resourceId: mediaType.id,
              title: mediaType.title,
              taskId: mediaType.taskId,
              totalLength: mediaType.totalLength,
              startTime,
              ...(mediaType.thumbnail ? { thumbnailUrl: mediaType.thumbnail } : {}),
            },
          } as any)
        }
        break
      case "website":
        openWebsite(mediaType.url || "", router.push)
        break
      case "image":
        router.push({ pathname: "/image-viewer", params: { imageUrl: mediaType.url } } as any)
        break
      case "audio":
        // Priority: 1) Backend offset 2) Saved position 3) Start from beginning
        // eslint-disable-next-line no-case-declarations
        const audioResourceKey = mediaType.id || mediaType.url || ""
        // eslint-disable-next-line no-case-declarations
        const audioSavedPosition = getLastPosition(audioResourceKey)
        router.push({
          pathname: "/audio-player",
          params: {
            audioUrl: mediaType.url || "",
            audioTitle: mediaType.title || "Audio File",
            autoplay: String(mediaType.autoplay ?? false),
            resourceId: mediaType.id,
            taskId: mediaType.taskId,
            totalLength: mediaType.totalLength,
            startTime: mediaType.offset
              ? String(parseTimestampToSeconds(mediaType.offset))
              : audioSavedPosition?.offset
                ? String(parseTimestampToSeconds(audioSavedPosition.offset))
                : "0",
          },
        } as any)
        break
      case "resource": {
        const isPdf = mediaType.resourceType === "pdf"
        const pdfSavedPosition = isPdf ? getLastPosition(mediaType.id) : undefined
        router.push({
          pathname: isPdf ? "/pdf-viewer" : "/file-viewer",
          params: {
            url: refreshedMediaUrl ?? mediaType.url,
            title: mediaType.title ?? `${mediaType.id}.${mediaType.resourceType}`,
            resourceId: mediaType.id,
            ...(pdfSavedPosition?.offset ? { startPage: pdfSavedPosition.offset } : {}),
          },
        } as any)
        break
      }
      case "todoList":
        router.push("/content-library" as any)
        break
      case "crisisLine":
        router.push("/crisis-line" as any)
        break
      case "roleSwitch":
        router.push("/change-role" as any)
        break
    }
  }

  return (
    <Pressable onPress={handlePress}>
      <Component />
    </Pressable>
  )
}

// MediaThumbnail.whyDidYouRender = true
